import { MoveDirection } from "@nens/shared-utils";

import { Folder, Layer } from "../../../types/publication";

/**
 * Drag-and-drop works on a FLAT list of ids instead of the layer tree, so a layer can be dragged
 * in and out of folders with a single framer-motion `Reorder.Group`.
 *
 * For this tree:
 *
 *   layers = [A, Folder F (open) [X, Y], B]
 *
 * the flat id list is:
 *
 *   ["A", "F", "X", "Y", "folder-end:F", "B"]
 *
 * An open folder's children follow its header, and a `folder-end:<id>` marker closes the folder.
 * An id sitting between a folder header and its marker is a child of that folder; everything else
 * is a root item. Closed folders are just their header id.
 *
 * Dragging flow (see `use-layer-drag.ts`):
 *   1. `buildFlatOrder` (tree -> flat ids) feeds the `Reorder.Group`, `buildRenderRows` renders
 *      the same rows.
 *   2. On every drag, we get a new flat id list back.
 *   3. `idsToPlacements` turns it into `Placement`s: which folder (if any) each id now sits in.
 *   4. `rebuildLayerTree` rebuilds the layer tree from those placements - first for the live
 *      preview, and on drop for the store (`createReorderFlatLayers`).
 */

/** Where an id ends up in the tree: at the root, or inside the folder `folderId`. */
export type Placement = { id: string; folderId?: string };

/** Folder id -> whether that folder is open (i.e. its children are part of the flat id list). */
export type FolderOpenById = Map<string, boolean>;

const FOLDER_END_PREFIX = "folder-end:";

export function getFolderEndMarkerId(folderId: string) {
  return `${FOLDER_END_PREFIX}${folderId}`;
}

function getFolderIdFromEndMarker(id: string): string | null {
  return id.startsWith(FOLDER_END_PREFIX) ? id.slice(FOLDER_END_PREFIX.length) : null;
}

/** Turns the layer tree into the flat id list (see the module comment above). */
export function buildFlatOrder(layers: Array<Layer | Folder>): string[] {
  const ids: string[] = [];
  for (const item of layers) {
    ids.push(item.id);
    if (item.type === "folder" && item.opened) {
      for (const child of item.layers) {
        if (child.type === "layer") ids.push(child.id);
      }
      ids.push(getFolderEndMarkerId(item.id));
    }
  }
  return ids;
}

/**
 * Reorders a flat list of ids as if the selected ids were dragged within the same container.
 * This is used by menu actions to decide whether a move target would be a no-op.
 */
export function reorderIds(
  container: Array<{ id: string }>,
  selectedIds: string[],
  direction: MoveDirection
): string[] {
  const selectedIdSet = new Set(selectedIds);
  const selected = container.filter((item) => selectedIdSet.has(item.id)).map((item) => item.id);
  const remaining = container.filter((item) => !selectedIdSet.has(item.id)).map((item) => item.id);

  if (selected.length === 0) {
    return container.map((item) => item.id);
  }

  if (direction === "top") {
    return [...selected, ...remaining];
  }

  if (direction === "bottom") {
    return [...remaining, ...selected];
  }

  const selectedIndices = container.reduce<number[]>((indices, item, index) => {
    if (selectedIdSet.has(item.id)) {
      indices.push(index);
    }
    return indices;
  }, []);

  const targetIndex =
    direction === "up" ? selectedIndices[0] - 1 : selectedIndices[selectedIndices.length - 1] + 2;
  const removedBefore = container.reduce((count, item, index) => {
    if (index < targetIndex && selectedIdSet.has(item.id)) {
      return count + 1;
    }
    return count;
  }, 0);
  const insertIndex = Math.max(0, Math.min(targetIndex - removedBefore, remaining.length));

  return [...remaining.slice(0, insertIndex), ...selected, ...remaining.slice(insertIndex)];
}

export function hasSameOrder(first: string[], second: string[]) {
  return first.length === second.length && first.every((item, index) => item === second[index]);
}

export type RenderRow =
  | { kind: "folder-header"; folder: Folder }
  | { kind: "layer"; layer: Layer; folder?: Folder }
  | { kind: "folder-empty"; folder: Folder }
  | { kind: "folder-end"; folder: Folder };

/**
 * The rows to render, in the same order as `buildFlatOrder`. Rendered with one flat `.map()` (not
 * one per folder) so a row keeps its component instance, and its in-progress drag, when it moves
 * in or out of a folder.
 */
export function buildRenderRows(layers: Array<Layer | Folder>): RenderRow[] {
  const rows: RenderRow[] = [];
  for (const item of layers) {
    if (item.type === "layer") {
      rows.push({ kind: "layer", layer: item });
      continue;
    }

    rows.push({ kind: "folder-header", folder: item });
    if (!item.opened) continue;

    const childLayers = item.layers.filter((child): child is Layer => child.type === "layer");
    if (childLayers.length === 0) {
      rows.push({ kind: "folder-empty", folder: item });
    } else {
      for (const child of childLayers) {
        rows.push({ kind: "layer", layer: child, folder: item });
      }
    }
    rows.push({ kind: "folder-end", folder: item });
  }
  return rows;
}

/**
 * Turns a flat id list back into placements: ids between an open folder's header and its end
 * marker belong to that folder. Folder headers are always root items (folders can't be nested).
 *
 * Mid-drag, framer-motion swaps one adjacent pair per `onReorder` call, so a dragged folder header
 * can briefly sit inside another open folder's range. That must not end the other folder's range,
 * otherwise the ids after it would drop out of that folder.
 */
export function idsToPlacements(newIds: string[], folderOpenById: FolderOpenById): Placement[] {
  const placements: Placement[] = [];
  let currentFolderId: string | undefined;

  for (const id of newIds) {
    const markerFolderId = getFolderIdFromEndMarker(id);
    if (markerFolderId) {
      if (currentFolderId === markerFolderId) currentFolderId = undefined;
      continue;
    }

    if (folderOpenById.has(id)) {
      placements.push({ id });
      // Don't switch folders if one's already active - see the comment above.
      if (currentFolderId === undefined) {
        currentFolderId = folderOpenById.get(id) ? id : undefined;
      }
      continue;
    }

    placements.push({ id, folderId: currentFolderId });
  }

  return placements;
}

/**
 * Rebuilds the layer tree from placements. Used for the live drag preview (without touching the
 * store) and to commit the final order on drop (`createReorderFlatLayers`).
 *
 * Only folders that are open in `folderOpenById` get their children replaced. A folder that was
 * closed for this drag (see `isDraggingFolder` in `use-layer-drag.ts`) wasn't part of the flat
 * list, so its layers are kept as they are instead of being emptied.
 */
export function rebuildLayerTree(
  rootLayers: Array<Layer | Folder>,
  placements: Placement[],
  folderOpenById: FolderOpenById
): Array<Layer | Folder> {
  const byId = new Map<string, Layer | Folder>();
  const collect = (items: Array<Layer | Folder>) => {
    for (const item of items) {
      byId.set(item.id, item);
      if (item.type === "folder") collect(item.layers);
    }
  };
  collect(rootLayers);

  const newRoot: Array<Layer | Folder> = [];
  const folderChildren = new Map<string, Layer[]>();

  for (const { id, folderId } of placements) {
    const item = byId.get(id);
    if (!item) continue;

    if (folderId) {
      if (!folderChildren.has(folderId)) folderChildren.set(folderId, []);
      // Folders can't be nested, so a folder's child is always a layer.
      if (item.type === "layer") folderChildren.get(folderId)?.push(item);
    } else {
      newRoot.push(item);
    }
  }

  return newRoot.map((item) => {
    if (item.type !== "folder" || !folderOpenById.get(item.id)) return item;
    return { ...item, layers: folderChildren.get(item.id) ?? [] };
  });
}
